import React, { useContext, useEffect, useState } from 'react';
import { getClass, getInstitutionById, updateTeacher, deleteTeachers } from '../../../actions';
import { Context } from '../../../defaults';
import { Descriptions, Card, Tooltip, Popconfirm, Form, Switch, Select, message } from 'antd';
import { CarryOutTwoTone, DeleteTwoTone, EditOutlined, EditTwoTone, CheckOutlined, CloseOutlined } from '@ant-design/icons';
import { emailRegex, latinRegex, phoneRegex, studyYearRegex } from '../../../common';
import TextArea from 'antd/lib/input/TextArea';
import MaskedInput from 'antd-mask-input';

const TeachersInfo = ({
  selectedTeacher,
  setSelectedTeacher,
  edit,
  setEdit,
  institutions,
  classes,
  filterInstitutionsByTypeId,
  selectedInstitutionType,
  onSelectInstitution,
  onSelectTeacher }) => {

  const { dictionary, language, setLoading } = useContext(Context);
  const [currentClass, setCurrentClass] = useState('');
  const [currentInstitution, setCurrentInstitution] = useState();

  const confirm = () => {
    deleteTeachers(selectedTeacher.id).then((res) => {
      message.success(dictionary?.OperationSuccessful[language]);
      filterInstitutionsByTypeId(null);
    });
  };

  const saveEditedTeacher = () => {
    if (
      selectedTeacher.lastName &&
      selectedTeacher.firstName &&
      !!selectedTeacher.phone.match(phoneRegex) &&
      !!selectedTeacher.users.login.match(latinRegex) &&
      !!selectedTeacher.users.email.match(emailRegex) &&
      selectedTeacher.institutionId &&
      !!selectedTeacher.studyYear.match(studyYearRegex)
    ) {
      setEdit(false);
      updateTeacher(selectedTeacher).then((res) => {
        message.success(dictionary?.SuccessfullyChanged[language])
        filterInstitutionsByTypeId(selectedInstitutionType);
        onSelectInstitution(selectedTeacher.institutionId);
        onSelectTeacher(selectedTeacher.id);
      });
    } else {
      message.error(dictionary?.FillTheAllRequiredFields[language]);
    }
  };


  const onChange = ({ name, value, target }) => {
    if (name === 'active') {
      setSelectedTeacher({
        ...selectedTeacher,
        users: {
          ...selectedTeacher.users, [name]: value ? 1 : 0
        },
      });
    } else if (!!target && (target.name === 'login' || target.name === 'email')) {
      setSelectedTeacher({
        ...selectedTeacher,
        users: { ...selectedTeacher.users, [target.name]: target.value },
      })
    } else {
      setSelectedTeacher({ ...selectedTeacher, [name]: value });
    }
  }

  const onInputChange = ({ target }) => {
    const { name, value } = target;
    setSelectedTeacher({ ...selectedTeacher, [name]: value });
  }

  const rules = (value, fieldName = null) => {
    if (fieldName === 'login') {
      if (!selectedTeacher && !selectedTeacher.users.login.match(latinRegex)) {
        return { validateStatus: 'error', help: dictionary?.OnlyLatinAndNumber[language] };
      } else {
        return { validateStatus: 'success', help: null };
      }
    } else if (fieldName === 'email') {
      if (!selectedTeacher && !selectedTeacher.users.email.match(emailRegex)) {
        return { validateStatus: 'error', help: dictionary?.InvalidEmail[language] };
      } else {
        return { validateStatus: 'success', help: null };
      }
    } else if (fieldName === 'studyYear') {
      const years = value.split('/');
      if (!selectedTeacher.studyYear.match(studyYearRegex)) {
        return { validateStatus: 'error', help: dictionary?.StudyYearField[language] };
      }
      if (years[0] > years[1]) {
        return {
          validateStatus: 'error',
          help: dictionary?.StudyYearFromLessThanTo[language],
        };
      }
      if (
        Number.isInteger(parseInt(years[0])) &&
        Number.isInteger(parseInt(years[1])) &&
        years[1] - years[0] !== 1
      ) {
        return {
          validateStatus: 'error',
          help: dictionary?.StudyYearValidate[language],
        };
      }
      else {
        return { validateStatus: 'success', help: null };
      }
    } else if (fieldName === 'phone') {
      if (!selectedTeacher && !selectedTeacher.phone.match(phoneRegex)) {
        return { validateStatus: 'error', help: dictionary?.InvalidPhone[language] };
      } else {
        return { validateStatus: 'success', help: null };
      }
    } else {
      if (!value) return { validateStatus: 'error', help: dictionary?.RequiredField[language] };
      else return { validateStatus: 'success', help: null };
    }
  };

  useEffect(() => {
    getClass(selectedTeacher.classId).then(({ data }) => setCurrentClass([data]));
    getInstitutionById(selectedTeacher.institutionId).then(({ data }) => setCurrentInstitution(data));
  }, [selectedTeacher])

  return (
    <React.Fragment>
      <Card
        style={{ marginTop: '30px' }}
        actions={[
          <Tooltip placement='top' title={dictionary?.Delete[language]}>
            <Popconfirm
              placement='bottom'
              title={dictionary?.AreYouSureToDelete[language]}
              onConfirm={confirm}
              okText={dictionary?.Yes[language]}
              cancelText={dictionary?.No[language]}
            >
              <DeleteTwoTone key='delete' twoToneColor='#eb2f96' />
            </Popconfirm>
          </Tooltip>,
          <Tooltip placement='top' title={dictionary?.Edit[language]}>
            {
              !edit &&
              <EditTwoTone
                key='edit'
                onClick={() => setEdit(true)}
              />
            }
            {
              edit &&
              <CarryOutTwoTone
                key='edit'
                onClick={saveEditedTeacher}
              />
            }

          </Tooltip>
        ]}
      >
        <Descriptions size='default' title={dictionary?.EditTeacher[language]} bordered column={4}>

          <Descriptions.Item label={dictionary?.UserLogin[language]} span={4}>
            {edit && (
              <Form.Item hasFeedback {...rules(selectedTeacher.users.login, 'login')}>
                <TextArea
                  disabled
                  style={{ width: '100%' }}
                  maxLength={100}
                  showCount={true}
                  size="middle"
                  name="login"
                  onChange={onChange}
                  value={selectedTeacher.users.login}
                  placeholder={dictionary?.UserLogin[language]}
                />
              </Form.Item>
            )}
            {
              !edit && !!selectedTeacher.users && selectedTeacher.users?.login
            }
          </Descriptions.Item>

          <Descriptions.Item label={dictionary?.Email[language]} span={4}>
            {edit && (
              <Form.Item hasFeedback {...rules(selectedTeacher.users.email, 'email')}>
                <TextArea
                  disabled
                  style={{ width: '100%' }}
                  size="middle"
                  name="email"
                  onChange={onChange}
                  maxLength={100}
                  value={selectedTeacher.users.email}
                  showCount={true}
                  placeholder={dictionary?.Email[language]}
                />
              </Form.Item>
            )}
            {
              !edit && !!selectedTeacher.users && selectedTeacher.users?.email
            }
          </Descriptions.Item>

          <Descriptions.Item label={dictionary?.Institution[language]} span={4}>
            {
              edit &&
              <Form.Item hasFeedback  {...rules(selectedTeacher.institutionId)}>
                <Select
                  disabled
                  style={{ width: '100%' }}
                  placeholder={dictionary?.Institution[language]}
                  value={selectedTeacher.institutionId}
                  size='middle'
                  name='institutionId'
                  onChange={(value) => onChange({ name: 'institutionId', value })}
                  allowClear={true}>
                  {
                    !!institutions &&
                    institutions.map((el) => {
                      return (
                        <Select.Option value={el.id} key={el.id}>
                          {`${el.name}, ${el.city}, ${el.address}`}
                        </Select.Option>
                      );
                    })
                  }
                </Select>
              </Form.Item>
            }
            {
              !edit && !!currentInstitution && currentInstitution.name
            }
          </Descriptions.Item>

          <Descriptions.Item label={dictionary?.YearOfStudy[language]} span={2}>
            {edit && (
              <Form.Item hasFeedback {...rules(selectedTeacher.studyYear, 'studyYear')}>
                <MaskedInput
                  name="studyYear"
                  style={{ width: '100%' }}
                  mask="11/11"
                  size="middle"
                  placeholder="xx/xx"
                  onChange={onInputChange}
                  value={selectedTeacher.studyYear}
                />
              </Form.Item>
            )}
            {!edit && !!selectedTeacher && selectedTeacher.studyYear}
          </Descriptions.Item>

          <Descriptions.Item label={dictionary?.Class[language]} span={2}>
            {
              edit &&
              <Form.Item hasFeedback>
                <Select
                  style={{ width: '100%' }}
                  placeholder={dictionary?.Class[language]}
                  value={selectedTeacher.classId}
                  size='middle'
                  name='classId'
                  onChange={(value) => onChange({ name: 'classId', value })}
                  allowClear={true}>
                  {
                    classes.length >= 1 ?
                      classes &&
                      classes.map((el) => {
                        return (
                          <Select.Option value={el.id} key={el.id}>
                            {el.year}{el.letter}
                          </Select.Option>
                        );
                      })
                      :
                      !!currentClass && currentClass.map((el) => {
                        return (
                          <Select.Option value={el.id} key={el.id}>
                            {el.year}{el.letter}
                          </Select.Option>
                        );
                      })
                  }
                </Select>
              </Form.Item>
            }
            {
              !edit && !!currentClass && (currentClass[0].year + currentClass[0].letter)
            }
          </Descriptions.Item>

          <Descriptions.Item label={dictionary?.LastName[language]} span={2}>
            {
              edit &&
              <Form.Item hasFeedback  {...rules(selectedTeacher.lastName)}>
                <TextArea
                  style={{ width: '100%' }}
                  maxLength={100}
                  showCount={true}
                  size='middle'
                  name='lastName'
                  onChange={onInputChange}
                  value={selectedTeacher.lastName}
                  placeholder={dictionary?.LastName[language]}
                />
              </Form.Item>
            }
            {
              !edit && selectedTeacher.lastName
            }
          </Descriptions.Item>

          <Descriptions.Item label={dictionary?.FirstName[language]} span={2}>
            {
              edit &&
              <Form.Item hasFeedback  {...rules(selectedTeacher.firstName)}>
                <TextArea
                  style={{ width: '100%' }}
                  maxLength={100}
                  showCount={true}
                  size='middle'
                  name='firstName'
                  onChange={onInputChange}
                  value={selectedTeacher.firstName}
                  placeholder={dictionary?.FirstName[language]}
                />
              </Form.Item>
            }
            {
              !edit && selectedTeacher.firstName
            }
          </Descriptions.Item>

          <Descriptions.Item label={dictionary?.MiddleName[language]} span={2}>
            {
              edit &&
              <Form.Item hasFeedback>
                <TextArea
                  style={{ width: '100%' }}
                  maxLength={100}
                  showCount={true}
                  size='middle'
                  name='middleName'
                  onChange={onInputChange}
                  value={selectedTeacher.middleName}
                  placeholder={dictionary?.MiddleName[language]}
                />
              </Form.Item>
            }
            {
              !edit && selectedTeacher.middleName
            }
          </Descriptions.Item>

          <Descriptions.Item label={dictionary?.Phone[language]} span={2}>
            {
              edit &&
              <Form.Item hasFeedback {...rules(selectedTeacher.phone, 'phone')}>
                <MaskedInput
                  style={{ width: '100%' }}
                  size='middle'
                  value={selectedTeacher.phone}
                  onChange={onInputChange}
                  name="phone"
                  mask="+7 (111) 111-11-11"
                  placeholder="+7 (___) ___-__-__" />
              </Form.Item>
            }
            {
              !edit && selectedTeacher.phone
            }
          </Descriptions.Item>

          <Descriptions.Item label={dictionary?.IsActive[language]} span={4}>
            <Switch
              disabled={!edit}
              checked={!!selectedTeacher && selectedTeacher.users.active === 1}
              name='active'
              onChange={(value) => onChange({ name: 'active', value })}

              checkedChildren={<CheckOutlined />}
              unCheckedChildren={<CloseOutlined />}
            />
          </Descriptions.Item>

        </Descriptions>
      </Card>
    </React.Fragment>
  );
};

export default TeachersInfo;
